import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { useToast } from '../../context/ToastContext';
import {
  createEpgSource,
  deleteEpgSourceById,
  fetchEpgSettings,
  fetchEpgSources,
  notifyEpgSourcesUpdated,
  postEpgCountryDefaults,
  saveEpgSettingsApi,
  triggerEpgRefreshAll,
  updateEpgSource,
  type EpgSourceRow,
} from '../../api/epgApi';
import { getApiErrorMessage } from '../../api/http';
import { usePlaylist } from '../../context/PlaylistContext';
import { fetchPlaylistById, updatePlaylist } from '../../api/playlists';

/** Jak legacy `epg.js` — checkboxy krajów i mapowanie do `country_key` z API. */
const EPG_COUNTRY_GROUPS: string[][] = [
  ['Poland', 'United Kingdom', 'United States', 'Germany', 'France'],
  ['Italy', 'Spain', 'Canada', 'Australia', 'Netherlands'],
  ['Belgium', 'Sweden', 'Norway', 'Denmark', 'Finland'],
];

/** ISO 3166-1 alpha-2 — pliki w `public/flags/{lower}.svg` (UK → gb). */
const EPG_COUNTRY_FLAG_ISO: Record<string, string> = {
  Poland: 'PL',
  'United Kingdom': 'GB',
  'United States': 'US',
  Germany: 'DE',
  France: 'FR',
  Italy: 'IT',
  Spain: 'ES',
  Canada: 'CA',
  Australia: 'AU',
  Netherlands: 'NL',
  Belgium: 'BE',
  Sweden: 'SE',
  Norway: 'NO',
  Denmark: 'DK',
  Finland: 'FI',
};

function EpgCountryFlagGraphic({ countryLabel }: { countryLabel: string }) {
  const iso = EPG_COUNTRY_FLAG_ISO[countryLabel];
  if (!iso) return null;
  const code = iso.toLowerCase();
  const src = `${import.meta.env.BASE_URL}flags/${code}.svg`;
  return (
    <img
      src={src}
      alt=""
      width={36}
      height={24}
      className="rounded border flex-shrink-0 shadow-sm"
      style={{ objectFit: 'cover' }}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

function selectionFromSources(sources: EpgSourceRow[]): Set<string> {
  const active = new Set<string>();
  for (const s of sources) {
    if (!s.is_system_default) continue;
    if (Number(s?.is_active ?? 1) === 0) continue;
    const k = String(s.country_key || s.country || '')
      .trim()
      .toLowerCase();
    if (k) active.add(k);
  }
  return active;
}

export function EpgCountriesModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    void (async () => {
      try {
        const sources = await fetchEpgSources();
        if (cancelled) return;
        const active = selectionFromSources(sources);
        const next: Record<string, boolean> = {};
        for (const row of EPG_COUNTRY_GROUPS) {
          for (const c of row) {
            next[c] = active.has(c.toLowerCase());
          }
        }
        setChecked(next);
      } catch (e) {
        showToast(getApiErrorMessage(e, 'Failed to load EPG countries'), 'error');
        const next: Record<string, boolean> = {};
        for (const row of EPG_COUNTRY_GROUPS) {
          for (const c of row) next[c] = false;
        }
        setChecked(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, showToast]);

  async function onSave() {
    const selected = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([k]) => k);
    setBusy(true);
    try {
      const data = await postEpgCountryDefaults(selected);
      if (!data.success) {
        showToast(data.message || 'Save failed', 'error');
        return;
      }
      showToast(data.message || `Country EPG: ${data.enabled_count ?? 0} enabled`, 'success');
      notifyEpgSourcesUpdated();
      onClose();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Select EPG countries"
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onSave()}>
            Save
          </button>
        </>
      }
    >
      <div className="row">
        {EPG_COUNTRY_GROUPS.map((col, ci) => (
          <div key={ci} className="col-md-4">
            {col.map((country) => (
              <div key={country} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`epgCountry_${country}`}
                  checked={checked[country] ?? false}
                  onChange={(e) => setChecked((prev) => ({ ...prev, [country]: e.target.checked }))}
                />
                <label
                  className="form-check-label d-inline-flex align-items-center gap-2"
                  htmlFor={`epgCountry_${country}`}
                >
                  <EpgCountryFlagGraphic countryLabel={country} />
                  <span>{country}</span>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
    </SimpleModal>
  );
}

export function EpgExternalSourceModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const [sources, setSources] = useState<EpgSourceRow[]>([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [compressed, setCompressed] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [retentionDays, setRetentionDays] = useState('7');
  const [busy, setBusy] = useState(false);

  const manageable = useMemo(() => sources.filter((s) => !s.is_system_default), [sources]);

  const reload = useCallback(async () => {
    try {
      setSources(await fetchEpgSources());
    } catch (e) {
      setSources([]);
      showToast(getApiErrorMessage(e, 'Failed to load EPG sources'), 'error');
    }
  }, [showToast]);

  useEffect(() => {
    if (!show) return;
    void reload();
    setName('');
    setUrl('');
    setCompressed(false);
    setSelectedId('');
    setRetentionDays('7');
  }, [show, reload]);

  useEffect(() => {
    const id = Number(selectedId);
    if (!id) {
      setRetentionDays('7');
      return;
    }
    const src = manageable.find((s) => s.id === id);
    const days = Number(src?.retention_days ?? 7);
    setRetentionDays(String(Number.isFinite(days) && days > 0 ? days : 7));
  }, [selectedId, manageable]);

  async function onAdd() {
    const u = url.trim();
    if (!u) {
      showToast('Enter an EPG source URL', 'warning');
      return;
    }
    let n = name.trim();
    if (!n) {
      try {
        n = new URL(u).hostname;
      } catch {
        n = `EPG ${new Date().toLocaleString()}`;
      }
    }
    setBusy(true);
    try {
      const result = await createEpgSource({
        name: n,
        url: u,
        is_active: 1,
        compressed: compressed ? 1 : 0,
        retention_days: 7,
      });
      if (result.success) {
        showToast('EPG source added', 'success');
        setName('');
        setUrl('');
        setCompressed(false);
        await reload();
        notifyEpgSourcesUpdated();
        onClose();
      } else {
        showToast(result.message || 'Error', 'error');
      }
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Request failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onForceRefresh() {
    setBusy(true);
    try {
      showToast('Starting EPG refresh for all your sources...', 'info');
      const r = await triggerEpgRefreshAll();
      if (r.success) showToast(`EPG download started for ${r.sources_count ?? 0} sources`, 'success');
      else showToast(r.message || 'Refresh failed', 'error');
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Request failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const id = Number(selectedId);
    if (!id) {
      showToast('Select a source to delete', 'warning');
      return;
    }
    const src = sources.find((s) => s.id === id);
    if (src?.is_system_default) {
      showToast('Built-in country EPG sources cannot be removed here', 'warning');
      return;
    }
    setBusy(true);
    try {
      const result = await deleteEpgSourceById(id);
      if (result.success) {
        showToast('EPG source removed', 'success');
        setSelectedId('');
        await reload();
        notifyEpgSourcesUpdated();
        onClose();
      } else {
        showToast(result.message || 'Error', 'error');
      }
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Request failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveRetention() {
    const id = Number(selectedId);
    if (!id) {
      showToast('Select a source first', 'warning');
      return;
    }
    const days = Math.max(1, Math.min(60, Number(retentionDays) || 7));
    setBusy(true);
    try {
      const result = await updateEpgSource({ id, retention_days: days });
      if (!result.success) {
        showToast(result.message || 'Failed to update retention', 'error');
        return;
      }
      showToast(`Retention saved (${days} days)`, 'success');
      await reload();
      notifyEpgSourcesUpdated();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Request failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal show={show} onClose={onClose} title="External EPG source" size="lg">
      <div className="row g-3">
        <div className="col-md-6 pe-md-3">
          <h6 className="mb-3">Add external EPG source</h6>
          <div className="mb-2">
            <label className="form-label" htmlFor="epgSourceNameReact">
              Name
            </label>
            <input
              id="epgSourceNameReact"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My EPG source"
            />
          </div>
          <div className="mb-2">
            <label className="form-label" htmlFor="epgUrlReact">
              Url
            </label>
            <input
              id="epgUrlReact"
              className="form-control"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="http://domain.com/xmltv.php?username=test&password=test"
            />
          </div>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="epgCompressedReact"
              checked={compressed}
              onChange={(e) => setCompressed(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="epgCompressedReact">
              Compressed EPG (ex. gzip)
            </label>
          </div>
          <button type="button" className="btn btn-success" disabled={busy} onClick={() => void onAdd()}>
            Add
          </button>
        </div>
        <div className="col-md-6">
          <h6 className="mb-3">Manage external EPG sources</h6>
          <div className="mb-2">
            <label className="form-label" htmlFor="epgSourceListReact">
              Select EPG source
            </label>
            <select
              id="epgSourceListReact"
              className="form-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">No epg source selected</option>
              {manageable.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-secondary me-2 mt-2" disabled={busy} onClick={() => void onForceRefresh()}>
            Force refresh
          </button>
          <div className="mt-3">
            <label className="form-label" htmlFor="epgRetentionDaysReact">
              Retention (days)
            </label>
            <div className="input-group">
              <input
                id="epgRetentionDaysReact"
                type="number"
                className="form-control"
                min={1}
                max={60}
                value={retentionDays}
                disabled={!selectedId || busy}
                onChange={(e) => setRetentionDays(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={!selectedId || busy}
                onClick={() => void onSaveRetention()}
              >
                Save
              </button>
            </div>
          </div>
          <button type="button" className="btn btn-danger mt-2" disabled={busy} onClick={() => void onDelete()}>
            Delete
          </button>
        </div>
      </div>
    </SimpleModal>
  );
}

export function EpgSettingsModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { playlistId } = usePlaylist();
  const [sources, setSources] = useState<EpgSourceRow[]>([]);
  const [defaultSourceId, setDefaultSourceId] = useState('');
  const [refreshInterval, setRefreshInterval] = useState('24');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [serverShift, setServerShift] = useState('0');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    void (async () => {
      try {
        const [list, settings] = await Promise.all([fetchEpgSources(), fetchEpgSettings()]);
        if (cancelled) return;
        setSources(list);
        if (settings.success && settings.data) {
          const d = settings.data;
          setDefaultSourceId(d.default_source_id != null ? String(d.default_source_id) : '');
          setRefreshInterval(String(d.refresh_interval ?? 24));
          setAutoRefresh(d.auto_refresh == null || Number(d.auto_refresh) === 1);
        } else {
          setDefaultSourceId('');
          setRefreshInterval('24');
          setAutoRefresh(true);
        }
        if (playlistId) {
          try {
            const pl = await fetchPlaylistById(Number(playlistId));
            if (!cancelled) {
              const sh = Number(pl.epg_server_shift_hours ?? 0);
              setServerShift(String(Number.isFinite(sh) ? Math.max(-12, Math.min(12, sh)) : 0));
            }
          } catch {
            if (!cancelled) setServerShift('0');
          }
        } else if (!cancelled) {
          setServerShift('0');
        }
      } catch (e) {
        if (!cancelled) {
          showToast(getApiErrorMessage(e, 'Failed to load EPG settings'), 'error');
          setSources([]);
          setDefaultSourceId('');
          setRefreshInterval('24');
          setAutoRefresh(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playlistId, show, showToast]);

  async function onSave() {
    const interval = Math.min(168, Math.max(1, Number(refreshInterval) || 24));
    setBusy(true);
    try {
      showToast('Saving EPG settings...', 'info');
      const result = await saveEpgSettingsApi({
        default_source_id: defaultSourceId ? Number(defaultSourceId) : null,
        refresh_interval: interval,
        auto_refresh: autoRefresh ? 1 : 0,
      });
      if (!result.success) {
        showToast(result.message || 'Save failed', 'error');
        return;
      }
      showToast(`EPG settings saved (refresh every ${interval} hours)`, 'success');
      if (playlistId) {
        const shift = Math.max(-12, Math.min(12, Number(serverShift) || 0));
        const plRes = await updatePlaylist(Number(playlistId), { epg_server_shift_hours: shift });
        if (!plRes.success) {
          showToast(plRes.message || 'Failed to save EPG server shift', 'warning');
        } else {
          showToast(`EPG server shift saved (${shift >= 0 ? '+' : ''}${shift} h)`, 'success');
        }
      }
      if (autoRefresh) {
        showToast('Starting EPG fetch for all sources...', 'info');
        const r = await triggerEpgRefreshAll();
        if (r.success) showToast(`EPG download started for ${r.sources_count ?? 0} sources`, 'success');
        else showToast(r.message || 'Refresh trigger failed', 'warning');
      }
      notifyEpgSourcesUpdated();
      onClose();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Connection error'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="EPG Settings"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onSave()}>
            Save
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label" htmlFor="defaultEpgSourceReact">
          Default EPG source
        </label>
        <select
          id="defaultEpgSourceReact"
          className="form-select"
          value={defaultSourceId}
          onChange={(e) => setDefaultSourceId(e.target.value)}
        >
          <option value="">None</option>
          {sources.map((s) => (
            <option key={s.id} value={String(s.id)}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor="epgRefreshIntervalReact">
          EPG refresh interval (hours)
        </label>
        <input
          id="epgRefreshIntervalReact"
          type="number"
          className="form-control"
          min={1}
          max={168}
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(e.target.value)}
        />
      </div>
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="epgAutoRefreshReact"
          checked={autoRefresh}
          onChange={(e) => setAutoRefresh(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="epgAutoRefreshReact">
          Auto refresh EPG data
        </label>
      </div>
      {playlistId ? (
        <div className="mt-3">
          <label className="form-label" htmlFor="epgServerShiftReact">
            Shift server time (hours)
          </label>
          <input
            id="epgServerShiftReact"
            type="number"
            className="form-control"
            min={-12}
            max={12}
            step={1}
            value={serverShift}
            onChange={(e) => setServerShift(e.target.value)}
          />
          <div className="form-text">
            Extra offset (−12 to +12) applied to XMLTV / Xtream times on top of automatic timezone
            correction. Use this when Smarters (or similar players) show EPG at the wrong hour.
          </div>
        </div>
      ) : (
        <div className="form-text mt-3">Select a playlist to set EPG server time shift.</div>
      )}
    </SimpleModal>
  );
}
